import React from "react";
import ReactDOM from "react-dom";
import {
  RetailerInfo,
  FilterDepartmentsDropDown,
  FilterDepartmentsList,
  Rating,
  ReviewsList,
  RetailerDetails,
  OpeningHours,
} from "./";
import { Dealer, DealerDetails } from "../../models";
import { ContentImagePath } from "../../utils/ContentMediaPath";
import classNames from "classnames";
import { Breakpoint } from "../../utils/Breakpoints";
import throttle from "lodash/throttle";

declare var MarkerClusterer: any;

interface Props {
  dealer: Dealer;
  dealerDetails: DealerDetails;
}

interface State {
  breakpoint: string;
}

class RetailerMapSimple extends React.Component<Props, State> {
  map: google.maps.Map;
  marker: google.maps.Marker;
  infowindow: google.maps.InfoWindow;
  markers: google.maps.Marker[];
  bounds: google.maps.LatLngBounds;

  constructor(props: Props) {
    super(props);
    this.state = {
      breakpoint: Breakpoint(),
    };
    this.loadMap = this.loadMap.bind(this);
    this.createMarker = this.createMarker.bind(this);

    window.addEventListener(
      "resize",
      throttle(() => {
        this.setState({
          breakpoint: Breakpoint(),
        });
      }, 500)
    );
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    if (nextState.breakpoint !== this.state.breakpoint) {
      this.mapFixOnBreakpointChange(nextState.breakpoint);
    }

    if (nextProps !== this.props) {
      return true;
    } else {
      return false;
    }
  }

  componentDidUpdate(nextProps: Props) {
    if (nextProps !== this.props) {
      this.markers.map((marker) => {
        marker.setMap(null);
      });
      this.createMarker();
    }
  }

  componentDidMount() {
    const { dealer, dealerDetails } = this.props;
    this.loadMap(dealer, dealerDetails);
  }

  mapFixOnBreakpointChange(breakpoint: string) {
    const mapRef = this.refs.map;
    const node = ReactDOM.findDOMNode(mapRef);
    console.log(node);

    if (breakpoint === "xs" || breakpoint === "sm") {
      node.setAttribute("style", "overflow:hidden;position:relative;");
    } else {
      node.setAttribute("style", "overflow:hidden;");
    }
  }

  loadMap(dealer, dealerDetails) {
    const { geoLatitude, geoLongitude } = !!dealerDetails && dealerDetails;

    const mapRef = this.refs.map;
    const node = ReactDOM.findDOMNode(mapRef);
    const styledMapType = new google.maps.StyledMapType(
      [
        {
          elementType: "geometry.stroke",
          stylers: [
            {
              visibility: "off",
            },
          ],
        },
        {
          elementType: "labels",
          stylers: [
            {
              visibility: "simplified",
            },
          ],
        },
        {
          elementType: "labels.icon",
          stylers: [
            {
              visibility: "simplified",
            },
          ],
        },
        {
          elementType: "labels.text.fill",
          stylers: [
            {
              color: "#808080",
            },
            {
              visibility: "on",
            },
          ],
        },
        {
          featureType: "administrative",
          elementType: "geometry",
          stylers: [
            {
              visibility: "off",
            },
          ],
        },
        {
          featureType: "landscape",
          elementType: "geometry.fill",
          stylers: [
            {
              visibility: "simplified",
            },
          ],
        },
        {
          featureType: "landscape.man_made",
          elementType: "geometry.fill",
          stylers: [
            {
              visibility: "off",
            },
          ],
        },
        {
          featureType: "landscape.natural",
          elementType: "geometry.fill",
          stylers: [
            {
              color: "#ebebec",
            },
            {
              visibility: "on",
            },
          ],
        },
        {
          featureType: "landscape.natural.landcover",
          elementType: "geometry.fill",
          stylers: [
            {
              visibility: "off",
            },
          ],
        },
        {
          featureType: "landscape.natural.terrain",
          elementType: "geometry.fill",
          stylers: [
            {
              visibility: "off",
            },
          ],
        },
        {
          featureType: "poi",
          stylers: [
            {
              visibility: "off",
            },
          ],
        },
        {
          featureType: "poi.park",
          elementType: "geometry.fill",
          stylers: [
            {
              color: "#d8e2cd",
            },
            {
              visibility: "on",
            },
          ],
        },
        {
          featureType: "road",
          elementType: "labels.icon",
          stylers: [
            {
              visibility: "off",
            },
          ],
        },
        {
          featureType: "road.highway",
          elementType: "geometry.fill",
          stylers: [
            {
              color: "#d2d5da",
            },
            {
              visibility: "on",
            },
          ],
        },
        {
          featureType: "road.highway",
          elementType: "geometry.stroke",
          stylers: [
            {
              color: "#d1cdc1",
            },
            {
              visibility: "off",
            },
          ],
        },
        {
          featureType: "transit",
          stylers: [
            {
              visibility: "off",
            },
          ],
        },
        {
          featureType: "water",
          elementType: "geometry.fill",
          stylers: [
            {
              color: "#c8def4",
            },
            {
              visibility: "on",
            },
          ],
        },
      ],
      { name: "Styled Map" }
    );

    let zoom = 15;
    let lat = parseFloat(geoLatitude) || -26.7677105;
    let lng = parseFloat(geoLongitude) || 136.1240992;
    const center = new google.maps.LatLng(lat, lng);
    const mapConfig = {
      center: center,
      zoom: zoom,
      mapTypeControl: false,
      fullscreenControl: false,
    };
    this.map = new google.maps.Map(node, mapConfig);
    this.map.mapTypes.set("styled_map", styledMapType);
    this.map.setMapTypeId("styled_map");

    this.createMarker();
  }

  createMarker() {
    let markers = [];
    let bounds = new google.maps.LatLngBounds();
    let marker;
    const { dealer, dealerDetails } = this.props;

    const pinIcon = ContentImagePath("map-pin-blue.svg");
    const pinActiveIcon = ContentImagePath("map-pin-green2.svg");

    const infowindow = new google.maps.InfoWindow({
      maxWidth: 200,
    });

    const { geoLatitude, geoLongitude } = !!dealerDetails && dealerDetails;
    let lat = parseFloat(geoLatitude) || -38.1437848;
    let lng = parseFloat(geoLongitude) || 145.1249061;

    // center map to the selected department
    this.map.setCenter(new google.maps.LatLng(lat, lng));

    marker = new google.maps.Marker({
      position: { lat, lng },
      map: this.map,
      title: `${dealer.name} - ${dealerDetails.departmentName}`,
      icon: {
        url: pinActiveIcon,
        // This marker is 22 pixels wide by 40 pixels high.
        size: new google.maps.Size(46, 70),
        scaledSize: new google.maps.Size(46, 70),
        // The origin for this image is (0, 0).
        origin: new google.maps.Point(0, 0),
        // The anchor for this image is the base of the cross (11, 52).
        anchor: new google.maps.Point(23, 67),
      },
    });
    marker.set("dealer", dealer);
    markers.push(marker);

    this.markers = markers;
    this.bounds = bounds;
  }

  render() {
    const { dealer, dealerDetails } = this.props;
    const { name, id, state, newCarsUrl } = dealer;
    const { address, postCode, suburb, phone, departmentName, url } =
      dealerDetails;
    const directionsUrl =
      suburb &&
      `https://www.google.com/maps/dir/Current+Location/${address}+${postCode}`;
    const clearUrl = url && url.replace(/(^\w+:|^)\/\//, "");
    const clearPhone = phone && phone.replace(/[- )(]/g, "");

    const buttonText = classNames({
      "Book a test drive":
        departmentName === "Vehicle Sales" ||
        departmentName === "WRX STI Vehicle Sales" ||
        departmentName === "Electric",
      "Request a service": departmentName === "Service",
      "Visit website":
        departmentName === "Certified Collision Repairers" ||
        departmentName === "Motorsport Parts",
    });

    const buttonUrl = classNames({
      "/book-test-drive":
        departmentName === "Vehicle Sales" ||
        departmentName === "WRX STI Vehicle Sales" ||
        departmentName === "Electric",
      "/service-booking ": departmentName === "Service",
      "/accidents-breakdowns/collision-repairs":
        departmentName === "Certified Collision Repairers",
    });

    return (
      <div className="find-dealer-widget">
        <div className="find-dealer-widget__body">
          <div className="dealer-location">
            <div className="container">
              <div
                className={`dealer-details ${
                  dealer ? "dealer-is-selected" : ""
                }`}
              >
                <div className="dealer-details__inner">
                  <p className="dealer-name">{name}</p>
                  <p className="dealer-phone">
                    {!!url && (
                      <span>
                        <a href={`${url}`} target="_blank">
                          {clearUrl}
                        </a>
                        <br />
                      </span>
                    )}
                    <a href={`tel:${clearPhone}`}>{phone}</a>
                  </p>
                  <p className="dealer-address">
                    {address}
                    <br />
                    {`${suburb} ${state} ${postCode}`}
                    <br />
                    {directionsUrl && suburb && !dealer.isMobileDealer && (
                      <span className="dealer-direction">
                        <a href={directionsUrl} target="_blank">
                          Get Directions
                        </a>
                      </span>
                    )}
                  </p>
                  <OpeningHours
                    dealerDetails={dealerDetails}
                    collapsible={true}
                  />
                  <p className="dealer-cta">
                    {departmentName !== "Parts and Accessories" && (
                      <a
                        href={
                          departmentName === "Motorsport Parts"
                            ? url
                            : buttonUrl
                        }
                        target="_blank"
                        className="btn primary"
                      >
                        {buttonText}
                      </a>
                    )}
                    {departmentName === "Vehicle Sales" && !!newCarsUrl && (
                      <a
                        href={newCarsUrl}
                        target="_blank"
                        className="btn-stock"
                      >
                        View new car stock
                      </a>
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="dealers-map" ref="map" />
          </div>
        </div>
      </div>
    );
  }
}

export default RetailerMapSimple;
